/**
 * Determines if a given color (hex, rgb, or named) is dark
 * @param color - Color string (hex, rgb, rgba, or named color)
 * @returns true if the color is dark, false if light
 */
export const isColorDark = (color: string | undefined): boolean => {
    if (!color) return true; // Default to dark if no color

    let r = 0, g = 0, b = 0;

    // Handle hex colors
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        if (hex.length === 3) {
            // Short hex (#fff)
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else {
            // Full hex (#ffffff)
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        }
    }
    // Handle rgb/rgba colors
    else if (color.startsWith('rgb')) {
        const match = color.match(/\d+/g);
        if (match && match.length >= 3) {
            r = parseInt(match[0]);
            g = parseInt(match[1]);
            b = parseInt(match[2]);
        }
    }
    // Handle named colors (basic set)
    else {
        const namedColors: Record<string, [number, number, number]> = {
            'black': [0, 0, 0],
            'white': [255, 255, 255],
            'red': [255, 0, 0],
            'green': [0, 128, 0],
            'blue': [0, 0, 255],
            'yellow': [255, 255, 0],
            'cyan': [0, 255, 255],
            'magenta': [255, 0, 255],
            'gray': [128, 128, 128],
            'grey': [128, 128, 128],
            'transparent': [0, 0, 0]
        };
        
        const normalized = color.toLowerCase();
        if (namedColors[normalized]) {
            [r, g, b] = namedColors[normalized];
        } else {
            return true; // Default to dark for unknown colors
        }
    }

    // Calculate relative luminance using the formula from W3C
    // https://www.w3.org/TR/WCAG20-TECHS/G17.html
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Colors with luminance < 0.5 are considered dark
    return luminance < 0.5;
};

/**
 * Get the appropriate logo filter based on background color
 * @param backgroundColor - The background color to check
 * @returns CSS filter string to invert logo if needed
 */
export const getLogoFilter = (backgroundColor: string | undefined): string => {
    return isColorDark(backgroundColor) ? 'invert(1) brightness(2)' : 'none';
};
